import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { requestTyped } from '@xdocs/ws-protocol';
import type { ErrorAlert } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

export interface ImpersonationSessionState {
  impersonationSessionId: string;
  targetUserId: string;
  /** Display name for the banner — the caller passes the row's known
   * name/email at `start()` time (`impersonation.start`'s response carries
   * no name, only `targetUserId`, F58's frozen contract). */
  targetName: string;
  expiresAt: Date;
}

export interface ImpersonationContextValue {
  session: ImpersonationSessionState | null;
  /** Seconds remaining until the hard TTL, ticking every second; `0` once expired. */
  secondsRemaining: number;
  isStarting: boolean;
  isEnding: boolean;
  error: ErrorAlert | null;
  start: (targetUserId: string, targetName: string, reason: string) => Promise<void>;
  end: () => Promise<void>;
}

const ImpersonationContext = createContext<ImpersonationContextValue | null>(null);

export interface ImpersonationProviderProps {
  children: ReactNode;
}

/**
 * F58 view-as-user state: owns the `impersonation.start`/`.end` round-trip,
 * the hard-TTL countdown (`IMPERSONATION_TTL_MINUTES`, §15.1), and exposes
 * it to `ImpersonationBanner` + the F54 user-detail "Enter view-as" action.
 *
 * OPEN ISSUE (returned in this task's issues list): starting a view-as
 * session only stores its bookkeeping here — it does NOT swap the live
 * socket's effective identity. `impersonation.start`'s response carries a
 * fresh `accessToken` scoped to the target user (§15.1), but
 * `AuthContextValue` (`apps/web/src/auth/AuthProvider.tsx`) exposes no
 * `setAccessToken`/equivalent an external provider can call, and swapping
 * the WS auth mid-session isn't part of `WsProvider`'s frozen contract
 * either. Wiring the actual "the app now acts as the target user" swap
 * (and restoring the staff token on `end()`/TTL expiry) is cross-package
 * work for the auth/WS owner or Integrate — this provider is written so
 * that hookup is additive (call `applyAccessToken` from `start`/`end` once
 * it exists) rather than a rewrite.
 */
export function ImpersonationProvider({ children }: ImpersonationProviderProps): ReactElement {
  const { wsClient } = useWs();
  const [session, setSession] = useState<ImpersonationSessionState | null>(null);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [isStarting, setIsStarting] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [error, setError] = useState<ErrorAlert | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    // No synchronous `setSecondsRemaining(0)` here when `!session`
    // (react-hooks/set-state-in-effect) — the exposed `secondsRemaining`
    // below derives `0` directly from `!session` instead of syncing it via
    // effect state, so there is nothing to reset when a session ends.
    if (!session) return;
    const tick = (): void => {
      const remaining = Math.max(0, Math.round((session.expiresAt.getTime() - Date.now()) / 1000));
      setSecondsRemaining(remaining);
      if (remaining === 0 && tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
        setSession(null);
      }
    };
    tick();
    tickRef.current = setInterval(tick, 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [session]);

  const start = useCallback(
    async (targetUserId: string, targetName: string, reason: string): Promise<void> => {
      setIsStarting(true);
      setError(null);
      try {
        const result = await requestTyped(wsClient, 'impersonation.start', { targetUserId, reason });
        setSession({
          impersonationSessionId: result.impersonationSessionId,
          targetUserId: result.targetUserId,
          targetName,
          expiresAt: new Date(result.expiresAt),
        });
      } catch (err) {
        setError(err as ErrorAlert);
        throw err;
      } finally {
        setIsStarting(false);
      }
    },
    [wsClient],
  );

  const end = useCallback(async (): Promise<void> => {
    if (!session) return;
    setIsEnding(true);
    setError(null);
    try {
      await requestTyped(wsClient, 'impersonation.end', { impersonationSessionId: session.impersonationSessionId });
      setSession(null);
    } catch (err) {
      setError(err as ErrorAlert);
      throw err;
    } finally {
      setIsEnding(false);
    }
  }, [wsClient, session]);

  const value = useMemo<ImpersonationContextValue>(
    () => ({ session, secondsRemaining: session ? secondsRemaining : 0, isStarting, isEnding, error, start, end }),
    [session, secondsRemaining, isStarting, isEnding, error, start, end],
  );

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}

export function useImpersonation(): ImpersonationContextValue {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error('useImpersonation must be used within an <ImpersonationProvider>');
  return ctx;
}
